import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Link } from "react-router";
import {
  AlertTriangle,
  Building2,
  Calendar,
  Check,
  CheckCircle2,
  ChevronDown,
  CreditCard,
  Handshake,
  MessageCircle,
  RotateCcw,
  TabletSmartphone,
  Ticket,
  UserCheck,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Button, Checkbox, HoverPopover, Label } from "@/components/ui";
import { buttonVariants } from "@/components/ui/button-variants";
import { cn } from "@/lib/utils";
import { formatDateTime, formatRelativeTime } from "@/lib/datetime";
import type { AdminNotification } from "@/services/adminNotificationService";
import {
  getNotificationLinks,
  type NotificationLinkLabel,
} from "./notificationLinks";
import { CATEGORY_BADGE, SEVERITY_SIGNAL } from "./notificationBadgeConfig";
import {
  formatNotificationCategory,
  formatNotificationTitle,
} from "./notificationLabels";
import { notificationMetaString } from "./notificationMetadata";

const LINK_ICONS: Record<NotificationLinkLabel, LucideIcon> = {
  openPayment: CreditCard,
  openBooking: Calendar,
  openHandshake: Handshake,
  openTicket: Ticket,
  openChat: MessageCircle,
  openDevice: TabletSmartphone,
  openStructure: Building2,
};

function buildDetailRows(
  t: TFunction<"adminNotifications">,
  notification: AdminNotification,
): { key: string; label: string; value: string }[] {
  const rows: { key: string; label: string; value: string }[] = [
    {
      key: "event",
      label: t("details.eventType"),
      value: t(`eventTypes.${notification.event_type}`, {
        defaultValue: notification.event_type,
      }),
    },
  ];
  const serialNumber = notificationMetaString(
    notification.metadata_json,
    "serial_number",
  );
  if (serialNumber) {
    rows.push({
      key: "serial",
      label: t("details.serialNumber"),
      value: serialNumber,
    });
  }
  if (notification.target_type && notification.target_id) {
    rows.push({
      key: "target",
      label: t("details.target"),
      value: `${t(`targetTypes.${notification.target_type}`, {
        defaultValue: notification.target_type,
      })} · ${notification.target_id}`,
    });
  }
  rows.push({
    key: "last",
    label: t("details.lastOccurred"),
    value: formatDateTime(notification.last_occurred_at),
  });
  return rows;
}

interface NotificationCardProps {
  notification: AdminNotification;
  isBusy: boolean;
  showActions?: boolean;
  selectable?: boolean;
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
  onAcknowledge: (id: string) => void;
  onResolve: (id: string) => void;
  onReopen: (id: string) => void;
}

export function NotificationCard({
  notification,
  isBusy,
  showActions = true,
  selectable = false,
  selected = false,
  onSelectedChange,
  onAcknowledge,
  onResolve,
  onReopen,
}: NotificationCardProps) {
  const { t } = useTranslation("adminNotifications");
  const [expanded, setExpanded] = useState(false);
  const isResolved = notification.status === "resolved";
  const signal = SEVERITY_SIGNAL[notification.severity];
  const categoryBadge =
    notification.category in CATEGORY_BADGE
      ? CATEGORY_BADGE[
          notification.category as keyof typeof CATEGORY_BADGE
        ]
      : null;
  const CategoryIcon = categoryBadge?.icon ?? AlertTriangle;
  const displayTitle = formatNotificationTitle(t, notification);
  const links = getNotificationLinks(notification);
  const detailRows = expanded ? buildDetailRows(t, notification) : [];

  return (
    <article
      className={cn(
        "group relative transition-colors duration-150",
        selected
          ? "bg-primary-50/60 dark:bg-primary-50/40"
          : "hover:bg-surface-50 dark:hover:bg-surface-100/50",
        expanded && !selected && "bg-surface-50/70 dark:bg-surface-100/40",
      )}
    >
      <span
        className={cn(
          "absolute inset-y-3 left-0 w-0.5 rounded-r-full",
          selected ? "bg-primary-500" : signal.stripe,
          isResolved && !selected && "opacity-40",
        )}
        aria-hidden
      />

      <div className="flex items-center gap-2 py-3 pl-3 pr-3 sm:gap-3 sm:pl-4 sm:pr-4">
        {selectable ? (
          <Label
            spacing="none"
            className="-my-3 flex w-8 shrink-0 cursor-pointer items-center justify-center self-stretch"
          >
            <Checkbox
              checked={selected}
              onChange={(event) => onSelectedChange?.(event.target.checked)}
              aria-label={t("bulk.selectOne", { title: displayTitle })}
              className="pointer-events-none"
            />
          </Label>
        ) : null}

        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-3 rounded-lg text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-0"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          aria-label={
            expanded
              ? t("card.collapse", { title: displayTitle })
              : t("card.expand", { title: displayTitle })
          }
        >
          <span className="relative shrink-0">
            <span
              className={cn(
                "flex h-9 w-9 items-center justify-center rounded-lg",
                isResolved
                  ? "bg-surface-100 text-surface-400 ring-1 ring-inset ring-surface-500/10"
                  : signal.icon,
              )}
              aria-hidden
            >
              <CategoryIcon className="h-4 w-4" />
            </span>
            {!isResolved && notification.severity === "critical" && (
              <span
                className={cn(
                  "absolute -right-0.5 -top-0.5 h-2.5 w-2.5 animate-pulse rounded-full ring-2 ring-surface-0",
                  signal.dot,
                )}
                aria-hidden
              />
            )}
          </span>

          <span className="min-w-0 flex-1 space-y-1">
            <span
              className={cn(
                "block truncate text-sm font-semibold leading-snug tracking-tight",
                isResolved ? "text-surface-500" : "text-surface-900",
              )}
            >
              {displayTitle}
            </span>
            <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-surface-500">
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 font-medium",
                  isResolved ? "text-surface-500" : signal.label,
                )}
              >
                <span
                  className={cn(
                    "h-1.5 w-1.5 rounded-full",
                    isResolved ? "bg-surface-400" : signal.dot,
                  )}
                  aria-hidden
                />
                {t(`severity.${notification.severity}`)}
              </span>
              <span className="text-surface-300" aria-hidden>
                ·
              </span>
              <span>{t(`status.${notification.status}`)}</span>
              <span className="text-surface-300" aria-hidden>
                ·
              </span>
              <span>{formatNotificationCategory(t, notification.category)}</span>
              {notification.occurrence_count > 1 ? (
                <>
                  <span className="text-surface-300" aria-hidden>
                    ·
                  </span>
                  <span className="tabular-nums">
                    {t("occurrences", { count: notification.occurrence_count })}
                  </span>
                </>
              ) : null}
            </span>
          </span>

          <time
            className="hidden shrink-0 text-xs tabular-nums text-surface-400 sm:block"
            dateTime={notification.last_occurred_at}
            title={formatDateTime(notification.last_occurred_at)}
          >
            {formatRelativeTime(notification.last_occurred_at)}
          </time>

          <ChevronDown
            className={cn(
              "h-4 w-4 shrink-0 text-surface-400 transition-transform duration-200",
              expanded && "rotate-180",
            )}
            aria-hidden
          />
        </button>

        {showActions || links.length > 0 ? (
          <div className="flex shrink-0 items-center justify-end gap-1 border-l border-surface-200 pl-2 sm:min-w-[7.5rem] sm:pl-3">
            {links.map((link) => {
              const LinkIcon = LINK_ICONS[link.label];
              const label = t(`actions.${link.label}`);
              return (
                <HoverPopover key={link.key} content={label}>
                  <Link
                    to={link.to}
                    className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
                    aria-label={label}
                  >
                    <LinkIcon className="h-4 w-4" />
                  </Link>
                </HoverPopover>
              );
            })}
            {showActions && !isResolved ? (
              <HoverPopover content={t("actions.resolve")}>
                <Button
                  variant="outline"
                  size="icon-sm"
                  isLoading={isBusy}
                  leftIcon={<Check className="h-4 w-4" />}
                  className="text-surface-500 hover:border-success-300 hover:bg-success-50 hover:text-success-700 dark:hover:bg-success-50"
                  aria-label={t("actions.resolve")}
                  onClick={() => onResolve(notification.id)}
                />
              </HoverPopover>
            ) : null}
            {showActions && isResolved ? (
              <HoverPopover content={t("actions.reopen")}>
                <Button
                  variant="outline"
                  size="icon-sm"
                  isLoading={isBusy}
                  leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
                  className="text-surface-500"
                  aria-label={t("actions.reopen")}
                  onClick={() => onReopen(notification.id)}
                />
              </HoverPopover>
            ) : null}
          </div>
        ) : null}
      </div>

      {expanded ? (
        <div className="space-y-4 pb-4 pl-[60px] pr-4 sm:pl-[76px] sm:pr-5">
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-surface-700">
            {notification.body}
          </p>

          <div className="rounded-lg border border-surface-200 bg-surface-0 px-4 py-3">
            <dl className="grid w-full grid-cols-[max-content_minmax(0,1fr)] items-start gap-x-6 gap-y-2">
              {detailRows.map((row) => (
                <div key={row.key} className="contents">
                  <dt className="whitespace-nowrap text-xs font-medium text-surface-500">
                    {row.label}
                  </dt>
                  <dd className="min-w-0 break-words text-[13px] text-surface-900">
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
            {(notification.acknowledged_at || notification.resolved_at) && (
              <div className="mt-3 space-y-1 border-t border-surface-200 pt-3 text-[13px] text-surface-500">
                {notification.acknowledged_at && (
                  <p className="flex items-center gap-2">
                    <UserCheck className="h-3.5 w-3.5 shrink-0 text-surface-400" aria-hidden />
                    {t("audit.acknowledged", {
                      admin:
                        notification.acknowledged_by_email ??
                        t("audit.unknownAdmin"),
                      date: formatDateTime(notification.acknowledged_at),
                    })}
                  </p>
                )}
                {notification.resolved_at && (
                  <p className="flex items-center gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-surface-400" aria-hidden />
                    {t("audit.resolved", {
                      admin:
                        notification.resolved_by_email ??
                        t("audit.unknownAdmin"),
                      date: formatDateTime(notification.resolved_at),
                    })}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-2">
              {links.map((link) => {
                const LinkIcon = LINK_ICONS[link.label];
                return (
                  <Link
                    key={link.key}
                    to={link.to}
                    className={buttonVariants({ variant: "outline", size: "sm" })}
                  >
                    <LinkIcon className="h-4 w-4 shrink-0" />
                    {t(`actions.${link.label}`)}
                  </Link>
                );
              })}
              {links.length === 0 && (
                <span className="text-xs text-surface-400">
                  {t("actions.noLinks")}
                </span>
              )}
            </div>

            {showActions && (
              <div className="flex flex-wrap gap-2 sm:justify-end">
                {notification.status === "open" && (
                  <Button
                    variant="outline"
                    size="sm"
                    isLoading={isBusy}
                    leftIcon={<UserCheck className="h-4 w-4" />}
                    onClick={() => onAcknowledge(notification.id)}
                  >
                    {t("actions.acknowledge")}
                  </Button>
                )}
                {notification.status !== "resolved" && (
                  <Button
                    variant="primary"
                    size="sm"
                    isLoading={isBusy}
                    leftIcon={<CheckCircle2 className="h-4 w-4" />}
                    onClick={() => onResolve(notification.id)}
                  >
                    {t("actions.resolve")}
                  </Button>
                )}
                {notification.status === "resolved" && (
                  <Button
                    variant="outline"
                    size="sm"
                    isLoading={isBusy}
                    leftIcon={<RotateCcw className="h-4 w-4" />}
                    onClick={() => onReopen(notification.id)}
                  >
                    {t("actions.reopen")}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </article>
  );
}
